import {
  ArrowBack,
  CloudUpload,
  Delete,
  Download,
  Image,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import {
  useSelectedChild,
  useSelectedFolder,
} from "../contexts/AppStateContext";
import { deletePhoto, getPhotosByChild } from "../services/photoService";
import type { Photo } from "../types/api";
import { formatFileSize } from "../utils/formatting";

const PhotoGallery: React.FC = () => {
  const { centerId, childId, folderId } = useParams<{
    centerId: string;
    childId: string;
    folderId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedChild = useSelectedChild();
  const selectedFolder = useSelectedFolder();

  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [folderPhotos, setFolderPhotos] = useState<Photo[]>([]);
  const [currentFolderName, setCurrentFolderName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Resolve folder name from multiple sources with priority order
  useEffect(() => {
    // Priority 1: location.state from navigation
    if (location.state?.folderName) {
      setCurrentFolderName(location.state.folderName);
      return;
    }

    // Priority 2: selectedFolder from AppState

    if (selectedFolder?.name) {
      setCurrentFolderName(selectedFolder.name);
      return;
    }

    // Priority 3: Extract from first photo (for refresh scenarios)
    if (folderPhotos.length > 0 && folderPhotos[0].folderName) {
      setCurrentFolderName(folderPhotos[0].folderName);
      return;
    }

    // Fallback
    setCurrentFolderName("Photo Folder");
  }, [location.state, selectedFolder, folderId, folderPhotos]);

  // Fetch photos for the specific folder
  useEffect(() => {
    const fetchFolderPhotos = async () => {
      if (!centerId || !childId || !folderId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch all photos for the child
        const allPhotos = await getPhotosByChild(centerId, childId);

        // Filter photos by folderId with fallback to date matching for backward compatibility
        const folderPhotos = allPhotos.filter((photo) => {
          // Primary: Check if photo has folderId and matches
          if (photo.folderId) {
            return photo.folderId === folderId;
          }

          // Fallback: Check metadata.folderId for photos created during transition
          if (photo.metadata?.folderId) {
            return photo.metadata.folderId === folderId;
          }

          // Legacy fallback: For old photos without folderId, match by uploadDate
          const photoDateKey =
            photo.metadata?.uploadDate ||
            photo.createdAt.toISOString().split("T")[0];
          return photoDateKey === folderId;
        });

        setFolderPhotos(folderPhotos);
      } catch (error) {
        console.error("Error fetching folder photos:", error);
        setFolderPhotos([]);
        setCurrentFolderName("");
      } finally {
        setLoading(false);
      }
    };

    fetchFolderPhotos();
  }, [centerId, childId, folderId, location.state]);

  const handleBack = () => {
    navigate(`/center/${centerId}`);
  };

  const handleUploadClick = () => {
    navigate(`/center/${centerId}/child/${childId}/folder/${folderId}/upload`, {
      state: { folderName: currentFolderName, folderId: folderId },
    });
  };

  const handleDownloadPhoto = (photo: Photo) => {
    const url = photo.finalUrl || photo.previewUrl || photo.originalUrl;
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleDeletePhotoClick = (photo: Photo) => {
    setPhotoToDelete(photo);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!photoToDelete) return;

    setDeleting(true);
    try {
      await deletePhoto(photoToDelete.id);

      // Remove from local state
      setFolderPhotos((prev) => prev.filter((p) => p.id !== photoToDelete.id));

      // Close dialog
      setDeleteDialogOpen(false);
      setPhotoToDelete(null);
    } catch (error) {
      console.error("Error deleting photo:", error);
      alert("Failed to delete photo. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPhotoToDelete(null);
  };

  const displayPhotos = folderPhotos.map((photo) => ({
    id: photo.id,
    name: photo.metadata?.originalFileName || `Photo ${photo.id}`,
    url: photo.finalUrl || photo.previewUrl || photo.originalUrl,
    uploadDate: photo.metadata.uploadDate,
    size: photo.metadata?.fileSize
      ? formatFileSize(photo.metadata.fileSize)
      : "",
    uploaded: true,
    uploading: false,
    progress: 100,
    downloadURL: photo.finalUrl || photo.previewUrl || photo.originalUrl,
    originalPhoto: photo,
  }));

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Header />
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />

      {/* Breadcrumb and Header */}
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to {selectedChild?.name || "Child"}
        </Button>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {currentFolderName || "Photo Folder"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {selectedChild?.name || "Child Name"} • {displayPhotos.length}{" "}
              photos
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUploadClick}
            >
              Upload Photos
            </Button>
          </Box>
        </Box>

        {/* Photo Grid */}
        {displayPhotos.length > 0 ? (
          <Grid container spacing={2}>
            {displayPhotos.map((photo) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={photo.id}>
                <Card
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      transform: "scale(1.02)",
                      "& .photo-overlay": { opacity: 1 },
                    },
                  }}
                  onClick={() => setSelectedPhoto(photo.id)}
                >
                  <Box
                    sx={{
                      position: "relative",
                      paddingTop: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={photo.url}
                      alt={photo.name}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />

                    {/* Action Overlay */}
                    <Box
                      className="photo-overlay"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: 0,
                        transition: "opacity 0.2s",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton sx={{ color: "white" }}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            sx={{ color: "white" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadPhoto(
                                folderPhotos.find((p) => p.id === photo.id)!,
                              );
                            }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            sx={{ color: "white" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhotoClick(
                                folderPhotos.find((p) => p.id === photo.id)!,
                              );
                            }}
                          >
                            <Delete sx={{ color: "#ff7167ff" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 0.5 }}
                    >
                      {photo.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {photo.uploadDate} • {photo.size}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* Empty state */
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Image sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No photos yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload photos to start building this folder
            </Typography>
          </Box>
        )}
      </Box>

      {/* Photo Detail Dialog */}
      <Dialog
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedPhoto && (
            <Box>
              <img
                src={displayPhotos.find((p) => p.id === selectedPhoto)?.url}
                alt="Selected photo"
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Photo?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this photo? This action cannot be
            undone.
          </Typography>
          {photoToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Photo: {photoToDelete.metadata?.fileName || photoToDelete.id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoGallery;
